import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { useEffect, useState } from "react";

const SIZE = 240;
const STROKE = 16;
const RADIUS = (SIZE - STROKE) / 2;
const CIRC = 2 * Math.PI * RADIUS;

const toneColors = {
  warning: "#B23A48",   // <60
  moderate: "#C89933",  // 60-80
  optimal: "#556B4F",   // >80
};

export default function Gauge({ score = 0, tone = "moderate", ...props }) {
  const color = toneColors[tone] || toneColors.moderate;
  const target = Math.max(0, Math.min(100, score));

  // animated numeric readout
  const mv = useMotionValue(0);
  const rounded = useTransform(mv, (v) => Math.round(v));
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const controls = animate(mv, target, { duration: 1.6, ease: [0.16, 1, 0.3, 1] });
    const unsub = rounded.on("change", (v) => setDisplay(v));
    return () => { controls.stop(); unsub(); };
  }, [target, mv, rounded]);

  const offset = CIRC * (1 - target / 100);

  return (
    <div
      className="relative"
      style={{ width: SIZE, height: SIZE }}
      {...props}
    >
      <svg width={SIZE} height={SIZE} className="-rotate-90">
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          stroke="#E2E8F0"
          strokeWidth={STROKE}
          fill="none"
        />
        <motion.circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          stroke={color}
          strokeWidth={STROKE}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={CIRC}
          initial={{ strokeDashoffset: CIRC }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span
          className="font-serif-display leading-none tabular-nums text-[#0F1E3D]"
          style={{ fontSize: 68, letterSpacing: "-0.02em" }}
          data-testid="gauge-score"
        >
          {display}
        </span>
        <span className="mt-2 overline text-[0.62rem]" style={{ color }}>
          out of 100
        </span>
      </div>
    </div>
  );
}
